import React, { useCallback, useContext, useEffect } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Device from "expo-device";
import * as ImagePicker from "expo-image-picker";
import * as Notifications from "expo-notifications";
import { EventSubscription } from "expo-modules-core";
import Constants from "expo-constants";
import { AuthManagerContext } from "../contexts/AuthManager";
import { listeners, MessageData } from "../configs/notifications-listeners";
import { useI18n } from "./i18n-hooks";
import { notificationEnabledState } from "../atoms/push-notifications";
import { useRecoilState } from "recoil";
import * as ExpoNotifications from "expo-notifications";
import { getNotificationData } from "../utils";

const dispatchMessage = (messageData: MessageData) => {
    if (listeners[messageData.messageType || ""]) {
        listeners[messageData.messageType || ""].forEach((l) => {
            l(messageData);
        });
    }
};

const registerForExpoPushNotificationsAsync = async (): Promise<string | null> => {
    if (Platform.OS === "android") {
        // There is an issue on android. All notif are sent on this channel (fcm_fallback_notification_channel),
        // so we rename it as Lift and set it as High to display it as heads-up notification
        await Notifications.setNotificationChannelAsync("fcm_fallback_notification_channel", {
            name: "Lift",
            importance: Notifications.AndroidImportance.HIGH,
            lightColor: "#FF231F7C",
        });
    }
    if (Device.isDevice) {
        const token = (
            await Notifications.getExpoPushTokenAsync({
                projectId: Constants.expoConfig?.extra?.eas.projectId,
            })
        ).data;
        return token;
    } else {
        //eslint-disable-next-line
        console.info("Must use physical device for Push Notifications");
        return null;
    }
};

export const usePushNotification = () => {
    const notificationListener = React.useRef<EventSubscription>();
    const { setAppExpoPushToken } = useContext(AuthManagerContext);
    const [notificationEnabled, setNotificationEnabled] = useRecoilState(notificationEnabledState);

    useEffect(() => {
        const asyncFunc = async () => {
            const { granted } = await ExpoNotifications.getPermissionsAsync();
            if (granted) {
                setNotificationEnabled(true);
            }
        };
        void asyncFunc();
    }, [setNotificationEnabled]);

    React.useEffect(() => {
        if (notificationEnabled) {
            void registerForExpoPushNotificationsAsync().then((token) => {
                if (token === null) {
                    setAppExpoPushToken("");
                } else {
                    setAppExpoPushToken(token);
                }
            });

            Notifications.setNotificationHandler({
                handleError: (id, error) => {
                    console.error("handle error", id, error);
                },
                handleNotification: async (notification) => {
                    // if silent is defined in data then we just want to handle it in the background without
                    // displaying any notification
                    if (!getNotificationData(notification.request.content)?.silent) {
                        return Promise.resolve({
                            shouldShowAlert: true,
                            shouldPlaySound: true,
                            shouldSetBadge: true,
                        });
                    } else {
                        return Promise.resolve({
                            shouldShowAlert: false,
                            shouldPlaySound: false,
                            shouldSetBadge: false,
                        });
                    }
                },
            });

            // This listener is fired whenever a notification is received while the app is foregrounded
            notificationListener.current = Notifications.addNotificationReceivedListener(
                (notification) => {
                    // console.log("receive notification", JSON.stringify(notification));
                    dispatchMessage(notification.request.content.data);
                },
            );

            return () => {
                if (notificationListener.current) {
                    Notifications.removeNotificationSubscription(notificationListener.current);
                }
            };
        }
    }, [notificationEnabled, setAppExpoPushToken]);

    return null;
};

const MISSION_TIMESHEET_NOTIFICSTION_KEY = "missionsTimesheetNotificationIdentifier";

// This hook is used to display a notification when the user has pending timesheets every Friday at 15h GMT
// To avoid triggering the notification when we don't know, we use undefined as initial value
export const useMissionsTimesheetNotification = (hasPendingTimesheets: boolean | undefined) => {
    const i18n = useI18n();
    const asyncStorageAccess = useCallback(
        async (hasPendingTimesheets: boolean) => {
            const notifIdentifier = await AsyncStorage.getItem(MISSION_TIMESHEET_NOTIFICSTION_KEY);
            if (!hasPendingTimesheets && notifIdentifier) {
                await Notifications.cancelScheduledNotificationAsync(notifIdentifier);
                await AsyncStorage.removeItem(MISSION_TIMESHEET_NOTIFICSTION_KEY);
            }
            if (hasPendingTimesheets && !notifIdentifier) {
                try {
                    const identifier = await Notifications.scheduleNotificationAsync({
                        content: {
                            title: i18n.t("screens.user.notifications.timesheetNotificationTitle"),
                            body: i18n.t("screens.user.notifications.timesheetNotificationBody"),
                            data: {
                                app_url: `${
                                    Constants.expoConfig?.extra?.scheme as string
                                }/app/timesheets`,
                            },
                        },
                        trigger: {
                            type: Notifications.SchedulableTriggerInputTypes.WEEKLY,
                            weekday: 6, // 6 is Friday
                            hour: 16, // 16 hour local time
                            minute: 0,
                        },
                    });
                    await AsyncStorage.setItem(MISSION_TIMESHEET_NOTIFICSTION_KEY, identifier);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log("error setting notification", e);
                }
            }
        },
        [i18n],
    );

    useEffect(() => {
        if (hasPendingTimesheets !== undefined) {
            void asyncStorageAccess(hasPendingTimesheets);
        }
    }, [hasPendingTimesheets, asyncStorageAccess]);
};

export const useCameraStatus = () => {
    const [status, setStatus] = React.useState<ImagePicker.PermissionStatus | null>(null);
    useEffect(() => {
        const _func = async () => {
            const { status: existingStatus } = await ImagePicker.getCameraPermissionsAsync();
            setStatus(existingStatus);
        };
        void _func();
    }, []);
    return status;
};

export const useLibraryStatus = () => {
    const [status, setStatus] = React.useState<ImagePicker.PermissionStatus | null>(null);
    useEffect(() => {
        const _func = async () => {
            const { status: existingStatus } = await ImagePicker.getMediaLibraryPermissionsAsync();
            setStatus(existingStatus);
        };
        void _func();
    }, []);
    return status;
};
